import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Stars = ({ value, size = 15 }) => (
  <span className="flex gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= Math.round(value) ? "fill-taupe text-taupe" : "text-charcoal/25"} />
    ))}
  </span>
);

export const ReviewSection = ({ slug }) => {
  const { user } = useAuth();
  const [data, setData] = useState({ average: 0, count: 0, reviews: [] });
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/products/${slug}/reviews`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!rating) {
      toast.error("Pick a star rating first.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/products/${slug}/reviews`, { rating, comment });
      toast.success("Thank you — your review is live.");
      setRating(0);
      setComment("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="review-section" className="mx-auto max-w-[1600px] px-6 pb-24 sm:px-10">
      <div className="grid grid-cols-1 gap-16 border-t border-black/10 pt-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-taupe">Reviews</p>
          <div className="flex items-end gap-4">
            <span data-testid="review-average" className="font-serif text-6xl font-light">{data.average || "—"}</span>
            <div className="pb-2">
              <Stars value={data.average} />
              <p data-testid="review-count" className="mt-1 text-xs uppercase tracking-[0.2em] text-ash">
                {data.count} {data.count === 1 ? "review" : "reviews"}
              </p>
            </div>
          </div>
          {user ? (
            <div className="mt-10 space-y-4" data-testid="review-form">
              <p className="text-xs uppercase tracking-[0.2em] text-ash">Share yours</p>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} data-testid={`star-${i}`} onClick={() => setRating(i)} aria-label={`${i} stars`}
                    className="transition-transform duration-200 hover:scale-110">
                    <Star size={24} className={i <= rating ? "fill-moss text-moss" : "text-charcoal/25"} />
                  </button>
                ))}
              </div>
              <textarea data-testid="review-comment" rows={3} placeholder="How is the piece living with you? (optional)"
                value={comment} onChange={(e) => setComment(e.target.value)}
                className="w-full rounded-md border border-charcoal/20 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
              <button data-testid="review-submit" onClick={submit} disabled={busy}
                className="rounded-full bg-charcoal px-8 py-3.5 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss disabled:opacity-60">
                Post review
              </button>
            </div>
          ) : (
            <p className="mt-10 text-sm font-light text-ash">
              <Link to="/auth" data-testid="review-signin-prompt" className="underline underline-offset-4 hover:text-charcoal">Sign in</Link> to share your review.
            </p>
          )}
        </div>
        <div className="lg:col-span-7">
          <div className="space-y-8">
            {data.reviews.length === 0 && (
              <p className="text-sm font-light text-ash">No reviews yet — be the first to gather one.</p>
            )}
            {data.reviews.map((r, i) => (
              <motion.div key={r.user_id + i} data-testid={`review-${i}`}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }}
                className="border-t border-black/10 pt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{r.name}</p>
                  <Stars value={r.rating} size={13} />
                </div>
                {r.comment && <p className="mt-2 max-w-xl text-sm font-light leading-relaxed text-ash">{r.comment}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
